import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { K2SyncService } from './k2-sync.service';

@Component({
    selector: 'gtw-k2-sync',
    templateUrl: './k2-sync.component.html',
    styleUrls: ['./k2-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class K2SyncComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  GENERAL_CONFIG: any = {};
  private headerData: any = {};
  private globalParams: any = {};
  private userSettings: any = {};
  filterParams: any = {};

  logged_in_user = '';
  jcd_key = '';
  loading = false;

  filteredTaxYearData: any[] = [];
  selectedTaxYear = '';

  scenarioDropDown: any[] = [];
  selectedScenario = '';

  formDropDown: any[] = [];
  selectedForm = '';

  loadParentMEDropdown = false;
  parentMEDropDown: any[] = [];
  selectedParentMes: any = '';
  loadingParentME = false;

  showPship = false;
  pshipDropDown: any[] = [];
  selectedPship: any = '';

  syncRunning = false;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Run K2 Sync Process',
    message: 'Are you sure to Run the K2 Sync Process?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private k2SyncService: K2SyncService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.hideWorkspaceFilters();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.gtwWorkspace.hideDataFilters.next(false);
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.GENERAL_CONFIG = this.headerData.generalConfigData || {};
    this.globalParams = this.headerData.globalParams || {};
    this.userSettings = this.headerData.userSettingsData || {};
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);
  }

  /**
   * The server-side screen config for ftc-k2-sync carries a filter_key
   * inherited from the activity, but this screen has its own cascading
   * dropdowns and does not use workspace data-filters.
   *
   * Previous approach cleared filter_key on the activeScreen, but that
   * was subject to an async race: DataFiltersComponent.loadFilters()
   * (triggered before this CE mounts) would resolve its promise AFTER
   * the clear and re-show the filter bar.
   *
   * Using hideDataFilters removes `<gtw-data-filters>` from the DOM
   * entirely via *ngIf, which is immune to the race. The subject is
   * reset to false in ngOnDestroy so other screens get their filters
   * back.
   */
  private hideWorkspaceFilters(): void {
    this.gtwWorkspace.hideDataFilters.next(true);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.logged_in_user = this.userSettings?.user?.sso_id || '';
    this.jcd_key = this.globalParams?.jcd_key || '';

    this.getTaxYearDropDown();
  }

  getTaxYearDropDown(): void {
    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = { action_code: 'e83e1x' };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        const taxYearData = _.filter(data?.jsonObject, (p: any) =>
          _.includes(['TAX_YEAR'], p.CODE_GROUP)
        );
        const currentYear = Number(this.globalParams?.tax_year) || new Date().getFullYear();
        this.filteredTaxYearData = taxYearData.filter(
          (ty: any) =>
            ty.CODE_GROUP == 'TAX_YEAR' &&
            Number(ty.CODE_DISP_DESC) <= currentYear &&
            Number(ty.CODE_DISP_DESC) >= currentYear - 5
        );
        this.cdr.detectChanges();
      });
  }

  getScenarioDropDown(): void {
    this.selectedScenario = '';
    this.selectedForm = '';
    this.formDropDown = [];
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.showPship = false;
    this.pshipDropDown = [];
    this.selectedPship = '';

    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year to get the Scenario.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }

    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = {
      action_code: 'va45va',
      p_tax_year: this.selectedTaxYear,
      p_jcd_key: this.jcd_key,
    };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.cdr.detectChanges();
      });
  }

  getFormDropdown(): void {
    this.selectedForm = '';
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.showPship = false;
    this.pshipDropDown = [];
    this.selectedPship = '';

    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = { action_code: 'abkcdl' };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.formDropDown = data?.jsonObject || [];
        _.remove(this.formDropDown, { PRIORITY_ORDER: 1 });
        this.cdr.detectChanges();
      });
  }

  getParentMeDropdown(): void {
    this.selectedParentMes = '';
    this.parentMEDropDown = [];
    this.loadParentMEDropdown = false;
    this.showPship = false;
    this.pshipDropDown = [];
    this.selectedPship = '';

    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }

    this.loadingParentME = true;
    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = {
      action_code: '07roqs',
      sso_id: this.logged_in_user,
      p_tax_year: this.selectedTaxYear,
      p_scenario: this.selectedScenario,
    };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.parentMEDropDown = _.filter(
          data?.jsonObject,
          (p: any) => p.LEVEL_GROUP === 'Parent ME'
        );
        this.loadParentMEDropdown = true;
        this.loadingParentME = false;
        this.cdr.detectChanges();
      });
  }

  getPshipDropdown(): void {
    this.selectedPship = '';
    this.showPship = false;
    this.pshipDropDown = [];

    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = {
      action_code: 'kae9bk',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      p_form: this.selectedForm,
      p_schedule: 'K2',
      parent_me_string: this.selectedParentMes,
    };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.pshipDropDown = data?.jsonObject || [];
        _.remove(this.pshipDropDown, { VALUE: -1 });
        this.showPship = true;
        this.cdr.detectChanges();
      });
  }

  runSyncProcess(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year to run the sync process.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Scenario to run the sync process.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    if (!this.selectedForm) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Form to run the sync process.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    if (!this.selectedParentMes) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the ME(s) to run the sync process.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    if (!this.selectedPship) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Partnership to run the sync process.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }

    const matchedPship = this.pshipDropDown.find(
      (item: any) => item.VALUE == this.selectedPship
    );
    if (matchedPship && matchedPship.COMP_DEFAULT == 'N') {
      this.gtwSnackbarService.openSnackBar(
        'Please set the Component Defaults before running the K2 Sync.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }

    this.confirmAction(
      'Run K2 Sync Process',
      'Are you sure to Run the K2 Sync Process?',
      () => this.initiateSync()
    );
  }

  private initiateSync(): void {
    this.syncRunning = true;
    this.cdr.detectChanges();

    const matchedPship = this.pshipDropDown.find(
      (item: any) => item.VALUE == this.selectedPship
    );

    const params: any = {
      sso_id: this.logged_in_user,
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      p_form: this.selectedForm,
      p_filer_key: this.selectedPship,
      p_filer_label: matchedPship ? matchedPship.LABEL : '',
    };

    const lockInfo = {
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      combination_key: this.selectedPship,
    };
    params.checkLockedbj = JSON.stringify([lockInfo]);

    const pdfEngineUrl =
      this.GENERAL_CONFIG.pdf_engine_api_url ||
      this.GENERAL_CONFIG.pdfEngine_url ||
      '';

    if (!pdfEngineUrl) {
      this.gtwSnackbarService.openSnackBar(
        'PDF Engine API URL is not configured.',
        'gtw-snackbar--red',
        true,
        4
      );
      this.syncRunning = false;
      this.cdr.detectChanges();
      return;
    }

    this.k2SyncService.initiateSync(pdfEngineUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.syncRunning = false;
        if (response.callSuccess != 1) {
          this.gtwSnackbarService.openSnackBar(
            response.errorMessage || 'Sync process failed.',
            'gtw-snackbar--red',
            true,
            4
          );
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Sync Process for K2 is initiated. Email will be sent, after the process is complete.',
            'gtw-snackbar--green',
            true,
            4
          );
        }
        this.cdr.detectChanges();
      },
      () => {
        this.syncRunning = false;
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while initiating sync. Please try again.',
          'gtw-snackbar--red',
          true,
          4
        );
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.selectedTaxYear = '';
    this.selectedScenario = '';
    this.scenarioDropDown = [];
    this.formDropDown = [];
    this.selectedForm = '';
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.pshipDropDown = [];
    this.selectedPship = '';
    this.showPship = false;
    this.cdr.detectChanges();
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }
}
